import { useDndContext, useDroppable } from "@dnd-kit/core";
import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";

import { Stage } from "components/ui/form_components/StageSelect/Stage";
import { LeadCard } from "./LeadCard";
import { shouldShowDropPlaceholder, StageColumn } from "./utils";

export const BOARD_COLUMN_WIDTH = 400;
export const BOARD_COLUMN_PADDING_X = 12;

// Roughly a card's height, so the cards below shift by about what the drop will cost them.
const DROP_PLACEHOLDER_HEIGHT = 96;

const Column = styled.div<{ isOver: boolean }>(({ theme, isOver }) => ({
  flex: `0 0 ${BOARD_COLUMN_WIDTH}px`,
  width: BOARD_COLUMN_WIDTH,
  background: isOver ? theme.colors.sysPrimarySubtle : theme.colors.sysBackgroundAlternative,
  border: `1px solid ${isOver ? theme.colors.sysPrimaryDefault : theme.colors.sysBorderDecorative}`,
  borderRadius: 8,
  display: "flex",
  flexDirection: "column",
  maxHeight: "calc(100vh - 220px)",
  // Fades rather than snaps, so crossing the gutter between two columns doesn't strobe.
  transition: "background-color 150ms ease-out, border-color 150ms ease-out",
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
}));

const Header = styled.div({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: `12px ${BOARD_COLUMN_PADDING_X}px 8px`,
});

const DropZone = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: `4px ${BOARD_COLUMN_PADDING_X}px 12px`,
  overflowY: "auto",
  flex: 1,
  minHeight: 80,
});

const placeholderIn = keyframes({
  from: { height: 0, opacity: 0 },
  to: { height: DROP_PLACEHOLDER_HEIGHT, opacity: 1 },
});

// Sits at the top of the list, matching where groupLeadsByStage lands a moved card.
// It deliberately does not follow the pointer: the board has no manual ordering, so
// a gap under the cursor would promise a position the drop cannot honour.
const DropPlaceholder = styled.div(({ theme }) => ({
  flexShrink: 0,
  height: DROP_PLACEHOLDER_HEIGHT,
  border: `1px dashed ${theme.colors.sysPrimaryDefault}`,
  borderRadius: 4,
  background: theme.colors.sysBackgroundDefault,
  animation: `${placeholderIn} 150ms ease-out`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
}));

const Empty = styled.div(({ theme }) => ({
  padding: "16px 4px",
  fontSize: 12,
  color: theme.colors.sysTextInactive,
  textAlign: "center",
}));

export function BoardColumn({ column }: { column: StageColumn }) {
  const { setNodeRef, isOver } = useDroppable({ id: column.stage.id });
  const { active } = useDndContext();

  const showPlaceholder = shouldShowDropPlaceholder(
    isOver,
    active ? String(active.id) : null,
    column.leads.map((lead) => lead.id),
  );

  // The whole column is the drop target and now the whole column highlights with it,
  // header band included, so the hit area and the affordance are the same shape.
  return (
    <Column ref={setNodeRef} isOver={isOver} data-testid={`board-column-${column.stage.id}`}>
      <Header>
        <Stage
          label={column.stage.name}
          color={column.stage.color}
          iconColor={column.stage.iconColor}
          iconName={column.stage.iconName}
          truncate
        />
      </Header>
      <DropZone>
        {showPlaceholder && <DropPlaceholder data-testid="board-drop-placeholder" />}
        {column.leads.length === 0 && !showPlaceholder && <Empty>No leads</Empty>}
        {column.leads.map((lead) => (
          <LeadCard key={lead.id} lead={lead} />
        ))}
      </DropZone>
    </Column>
  );
}
